// Sugalarm — cycle 328: the gentle "add a photo" avatar nudge.
//
// A soft, dismissible, NON-BLOCKING card (never a takeover modal) that
// appears when the active member has no avatar, captured with the FRONT
// (user-facing) camera on both the Android-tablet kiosk and mobile. It
// reuses the entire cycle-105 avatar pipeline as-is (heic2any → the
// cycle-87 BackgroundCropper at aspect 1 / 512 → avatarsApi.upload); the
// only new SPA surface is this card + its store + its App-level mount.
//
// Mirrors the cycle-316 alarm tour's structural conventions:
//   • device-local persistence (the avatarNudge store — no JSONB sync);
//   • renders NULL while an alarm episode is live (the alarm path wins
//     unconditionally; state stays mounted so it resumes after) and while
//     the bedside/alarm-clock face is up.
//
// TWO dismiss paths, deliberately different in strength:
//   • "Not now" / tap-away — SOFT: hides for the rest of this app session,
//     then gently returns on a later session (once-per-session).
//   • "Don't remind me again" — PERSISTENT: never shows again for that
//     member on that device.
// There is NO "done on upload/skip" flag — avatar presence itself is the
// resolved state, so a successful upload flips `avatar_id` non-null and
// the nudge naturally goes inert (we do NOT write the persistent opt-out
// on a successful upload).

import { toCroppableBlob } from "../../lib/croppableImage";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { useLocation } from "react-router-dom";

// Cycle 330.6: the avatar path moved off the shared background cropper onto
// a dedicated AvatarCropper (true 1:1 circle + zoom-out-to-fit + slider).
// BackgroundCropper is left untouched — a background must never letterbox.
import {
  ACCEPT_IMAGE_CAMERA,
  ACCEPT_IMAGE_GALLERY,
} from "../../lib/imageAccept";
import { AvatarCropper } from "../../components/AvatarCropper";
import { Button } from "../../components/ui/Button";
import { MemberAvatar } from "../../components/ui/MemberAvatar";
import * as avatarsApi from "../../api/avatars";
import {
  isAndroidPhoneNative,
  isIosPhoneNative,
} from "../../lib/alarmReliability";
import {
  isAvatarNudgeOptedOut,
  isAvatarNudgeShownThisSession,
  markAvatarNudgeShownThisSession,
  setAvatarNudgeOptOut,
} from "../../lib/avatarNudge";
import {
  isAlarmTourDone,
  isAlarmTourOpen,
  subscribeAlarmTour,
} from "../../lib/alarmTour";
import { isAndroidTabletClassRuntime } from "../../lib/deviceClass";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import type { ActiveMember } from "../../lib/types";
import styles from "./AvatarNudge.module.css";
import { openPhotoInput, usePhotoBlockedByAlarm } from "../../lib/nativeCamera";
import { cameraOnlyDevice } from "../../lib/photoSource";

// Cycle 87: HEIC → JPEG for the cropper lives in `lib/croppableImage.ts`
// (shared since cycle 457).

const ACCEPT = ACCEPT_IMAGE_GALLERY;

// The one canonical body copy (prompt deliverable 2.2 — verbatim), used on
// both surfaces. No relationship label, no PHI.
const BODY =
  "A face makes it easy to tell whose glucose is whose at a glance — handy on the shared kiosk.";

type Mode = "closed" | "open" | "cropping" | "done";

export default function AvatarNudge({
  variant,
  activeMember,
  episodeLive,
  clockActive,
  onAvatarUpdated,
}: {
  variant: FrameVariant;
  activeMember: ActiveMember | null;
  episodeLive: boolean;
  clockActive: boolean;
  /** Called after a successful upload so the active member re-resolves and
   *  `avatar_id` flips non-null even if the `member_update` frame is slow. */
  onAvatarUpdated: () => void;
}) {
  const tourOpen = useSyncExternalStore(subscribeAlarmTour, isAlarmTourOpen);
  const location = useLocation();

  const [mode, setMode] = useState<Mode>("closed");
  const [cropSource, setCropSource] = useState<Blob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Cycle 455: disabled on the kiosk while any alarm sounds (lib/nativeCamera).
  const photoBlocked = usePhotoBlockedByAlarm();

  const cardRef = useRef<HTMLDivElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  // The member we've already opened/retired for this mount+session (so the
  // nudge activates at most once per member per session).
  const handledMemberRef = useRef<string | null>(null);

  const memberId = activeMember?.member_id ?? null;
  const displayName = activeMember?.display_name ?? "";
  const noAvatar = activeMember != null && activeMember.avatar_id == null;

  // On the kiosk, only the camera-capable Android-tablet kiosk qualifies
  // (isAndroidTabletClassRuntime() — the same predicate that drives the
  // device's camera_capable self-report). The Pi + iPad kiosk never mount.
  const kioskExcluded =
    variant === "kiosk" && !isAndroidTabletClassRuntime();

  // Setup has settled for this surface: on the native mobile shells, the
  // cycle-316 alarm tour is done (so the nudge comes AFTER it) and not
  // currently open; on plain web / the kiosk, the tour never runs, so no
  // gate applies (only "tour not currently open", always true there).
  const nativeMobileShell = isIosPhoneNative() || isAndroidPhoneNative();
  const setupSettled =
    !tourOpen && (nativeMobileShell ? isAlarmTourDone() : true);

  const onPairingRoute = location.pathname.startsWith("/pair");

  const eligible =
    memberId != null &&
    noAvatar &&
    !kioskExcluded &&
    setupSettled &&
    !onPairingRoute &&
    !isAvatarNudgeOptedOut(memberId) &&
    !isAvatarNudgeShownThisSession(memberId);

  // Active-member switch: retire any showing for the previous member so the
  // new member is re-evaluated cleanly.
  useEffect(() => {
    if (
      handledMemberRef.current != null &&
      handledMemberRef.current !== memberId
    ) {
      handledMemberRef.current = null;
      setMode("closed");
      setCropSource(null);
      setError(null);
    }
  }, [memberId]);

  // Activation: transition closed → open exactly once per eligible member,
  // stamping the soft session marker so it appears at most once per session.
  useEffect(() => {
    if (mode !== "closed") return;
    if (!eligible || memberId == null) return;
    if (handledMemberRef.current === memberId) return;
    handledMemberRef.current = memberId;
    markAvatarNudgeShownThisSession(memberId);
    setMode("open");
  }, [mode, eligible, memberId]);

  // The active member gained an avatar (via upload or a partner device) —
  // the nudge is inert; close any open surface.
  useEffect(() => {
    if (activeMember != null && activeMember.avatar_id != null) {
      setMode("closed");
      setCropSource(null);
    }
  }, [activeMember]);

  // Tap-away = a soft dismiss. A capture-phase document listener that never
  // preventDefaults, so the underlying tap still lands (non-blocking).
  useEffect(() => {
    if (mode !== "open") return;
    const onDown = (e: PointerEvent) => {
      if (cardRef.current && !cardRef.current.contains(e.target as Node)) {
        if (memberId) markAvatarNudgeShownThisSession(memberId);
        setMode("closed");
      }
    };
    // Defer a tick so the click that opened the card can't self-dismiss it.
    const id = window.setTimeout(
      () => document.addEventListener("pointerdown", onDown, true),
      0,
    );
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("pointerdown", onDown, true);
    };
  }, [mode, memberId]);

  const onPicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file later
    if (!f) return;
    setError(null);
    try {
      const blob = await toCroppableBlob(f);
      setCropSource(blob);
      setMode("cropping");
    } catch {
      setError("Couldn't read that image. Try a different photo or format.");
    }
  };

  const onCropConfirm = async (blob: Blob, w: number, h: number) => {
    if (memberId == null) {
      setCropSource(null);
      setMode("closed");
      return;
    }
    setBusy(true);
    setError(null);
    const r = await avatarsApi.upload(memberId, blob, w, h, "avatar.jpg");
    setBusy(false);
    setCropSource(null);
    if (!r.ok) {
      setError(r.error.message);
      setMode("open");
      return;
    }
    // Success — the member_update frame already refreshes the avatar
    // everywhere; refresh locally too so `avatar_id` flips non-null. Do NOT
    // write the persistent opt-out (presence handles it).
    onAvatarUpdated();
    setMode("done");
    window.setTimeout(() => setMode("closed"), 1900);
  };

  const notNow = () => {
    if (memberId) markAvatarNudgeShownThisSession(memberId);
    setMode("closed");
  };

  const dontRemind = () => {
    if (memberId) setAvatarNudgeOptOut(memberId);
    setMode("closed");
  };

  if (mode === "closed") return null;
  // The alarm path + the bedside clock face win unconditionally: render null
  // while a live episode or clock mode is up (state stays mounted → resumes).
  if (episodeLive || clockActive) return null;

  const rootVariantClass = variant === "kiosk" ? styles.kiosk : styles.mobile;
  const cameraOnly = cameraOnlyDevice();

  if (mode === "cropping" && cropSource != null) {
    return (
      <AvatarCropper
        source={cropSource}
        outputMaxLongEdge={512}
        busy={busy}
        onConfirm={(blob, w, h) => void onCropConfirm(blob, w, h)}
        onCancel={() => {
          setCropSource(null);
          setMode("open");
        }}
      />
    );
  }

  if (mode === "done") {
    return (
      <div className={`${styles.toast} ${rootVariantClass}`} role="status">
        <svg
          className={styles.toastOk}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          aria-hidden
        >
          <path d="M20 6 9 17l-5-5" />
        </svg>
        Photo added
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      className={`${styles.nudge} ${rootVariantClass}`}
      role="dialog"
      aria-label="Add a photo"
    >
      <div className={styles.top}>
        <div className={styles.avWrap}>
          <MemberAvatar avatarId={null} name={displayName} size={52} shape="circle" />
          <span className={styles.camBadge} aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2.2">
              <path d="M4 8h3l1.5-2h7L17 8h3v11H4z" />
              <circle cx="12" cy="13" r="3.4" />
            </svg>
          </span>
        </div>
        <div className={styles.txt}>
          <h3>Add a photo for {displayName}?</h3>
          <p>{BODY}</p>
        </div>
      </div>

      {error != null ? <div className={styles.error}>{error}</div> : null}

      <div className={styles.acts}>
        <Button
          variant="primary"
          full
          onClick={() => openPhotoInput(cameraInputRef.current, "avatar")}
          disabled={photoBlocked}
        >
          📷 Take photo
        </Button>
        {/* Cycle 458: no gallery on the kiosk APK (lib/photoSource). */}
        {cameraOnly ? null : (
          <Button
            variant="ghost"
            full
            onClick={() => openPhotoInput(galleryInputRef.current, "avatar")}
            disabled={photoBlocked}
          >
            Choose image
          </Button>
        )}
      </div>

      <div className={styles.foot}>
        <button type="button" className={styles.linkbtn} onClick={notNow}>
          Not now
        </button>
        <button
          type="button"
          className={`${styles.linkbtn} ${styles.quiet}`}
          onClick={dontRemind}
        >
          Don&rsquo;t remind me again
        </button>
      </div>

      {/* Take photo → front/selfie lens (capture="user"); on the kiosk
          WebView this sets FileChooserParams.isCaptureEnabled(), the
          discriminator the native front-lens branch keys on. */}
      <input
        ref={cameraInputRef}
        type="file"
        /* Cycle 330.9: MUST be exactly "image/*". Capacitor literal-matches
           that string to decide whether to open the camera at all — an
           explicit type list silently falls through to the gallery. */
        accept={ACCEPT_IMAGE_CAMERA}
        capture="user"
        onChange={(e) => void onPicked(e)}
        hidden
      />
      {/* Choose image → the same input WITHOUT capture (gallery / chooser). */}
      {cameraOnly ? null : (
        <input
          ref={galleryInputRef}
          type="file"
          accept={ACCEPT}
          onChange={(e) => void onPicked(e)}
          hidden
        />
      )}
    </div>
  );
}
